import * as React from "react";

import {motion} from "framer-motion";

import {Icon as IconLiterals, Platform} from "@client/constants";
import {Link} from "@client/router";
import {zIndexClasses} from "@client/style";

import {Icon} from "@client/components/Icon";
import {classNames, reverse, server} from "@reactivated";

const styles = {
    menu: `${zIndexClasses.menu} absolute w-screen h-screen bg-black/30`,
    isMenuOpen: "",
    menuContent:
        "pt-[calc(env(safe-area-inset-top))] min-w-[250px] w-[20%] bg-white dark:bg-slate-900 h-full flex flex-col items-start overflow-hidden",
    logo: "h-[27px] pt-2.5 pr-2.5 self-end",
    menuItems:
        "menu-items space-y-[15px] short-screen:space-y-3 pt-5 pb-5 flex-1 overflow-y-scroll w-full flex flex-col",
    menuItem: "",
    menuItemLink: "pl-[25px] flex items-center cursor-pointer",
    menuItemIcon:
        "menu-item-icon w-10 h-10 rounded-[10px] border-[1.5px] border-current flex-[40px_0] mr-2.5 flex justify-center items-center",
    menuItemTitle: "flex-1 text-left font-normal text-stone-900 dark:text-slate-100",
} as const;

const variants = {
    menu: {
        open: {
            opacity: 1,
            display: "block",
            transition: {duration: 0.1, delayChildren: 0.1},
        },
        closed: {
            opacity: 0,
            transition: {delay: 0.1, delayChildren: 0.1},
            transitionEnd: {
                display: "none",
            },
        },
    },
    logo: {
        open: {
            opacity: 1,
            transition: {
                when: "beforeChildren",
                delay: 0.3,
                duration: 0.1,
                staggerChildren: 0.2,
            },
        },
        closed: {
            opacity: 0,
        },
    },
    menuContent: {
        open: (height = 1000) => ({
            clipPath: `circle(${
                height * 2 + 200
            }px at 20px calc(env(safe-area-inset-top) + 20px))`,
            transition: {
                type: "spring",
                stiffness: 40,
                restDelta: 2,
            },
        }),
        closed: {
            clipPath: "circle(0px at 20px calc(env(safe-area-inset-top) + 20px))",
            transition: {
                delay: 0.1,
                type: "spring",
                stiffness: 400,
                damping: 40,
            },
        },
    },
    menuItems: {
        open: {
            transition: {staggerChildren: 0.035, delayChildren: 0.075},
        },
        closed: {
            transition: {staggerChildren: 0.05, staggerDirection: -1},
        },
    },
    menuItemSimple: {
        open: {
            opacity: 1,
        },
        closed: {
            opacity: 0,
        },
    },
} as const;

const colors = ["#EF8165", "#50BD9C", "#EAAE63", "#9582C4", "#488EA5"];

type MenuLink = {
    icon: IconLiterals;
    link: string;
    title: string;
    showIf?: (
        profile: server.core.rpc.Profile.output | null,
        platform: Platform,
    ) => boolean;
    external?: boolean;
};

const homePage = {
    icon: "home",
    link: reverse("home_page"),
    title: "Home Page",
    showIf: () => [].length == 1 && window.cordova == null,
    external: true,
} as const;

// TODO: these should be locators, not reverse.
const AUTHENTICATED_LINKS: MenuLink[] = [
    {
        icon: "custom-0283-contacts",
        link: "/journal/",
        title: "Journal",
        showIf: (profile) => profile != null,
    },
    {
        icon: "custom-dumbbell",
        link: "/workouts/",
        title: "Workouts",
    },
    {
        icon: "0302-walk",
        link: "/club/",
        title: "Walking Club",
        showIf: (profile) => profile != null && profile.user.has_walking_club,
    },
    {
        icon: "0291-users",
        link: "/manager/",
        title: "My Clients",
        showIf: (profile) => profile != null && profile.user.is_manager,
    },
    {
        icon: "0393-calendar-31",
        link: "/planner/",
        title: "Planner",
        showIf: (profile) =>
            profile != null &&
            !profile.user.casted_dismissed_announcements.includes("METHOD"),
    },
    {
        icon: "leaf",
        link: "/foods/",
        title: "My Foods",
        showIf: (profile) => profile != null,
    },
    {
        icon: "star",
        link: "/recipes/",
        title: "Recipes",
        showIf: (profile) => profile != null,
    },
    {
        icon: "0492-pills",
        link: "/peptides/",
        title: "Peptides",
        showIf: (profile) => profile != null && profile.user.has_peptides,
    },
    {
        icon: "user",
        link: "/profile/",
        title: "Profile",
    },
    {
        icon: "bar-chart-o",
        link: "/reports/",
        title: "Reports",
        showIf: (profile) => profile != null && profile.user.is_reports_enabled,
    },
    // {
    //     icon: "send",
    //     link: "/invite/",
    //     title: "Invite",
    // },
    {
        icon: "envelope-o",
        link: "/contact/",
        title: "Support",
    },
    homePage,
    {
        icon: "custom-0767-exit-right",
        link: "/logout/",
        title: "Logout",
    },
];

const ANONYMOUS_LINKS: MenuLink[] = [
    {
        icon: "custom-0763-enter-right",
        link: "/login/",
        title: "Login",
    },
    {
        icon: "pencil-square-o",
        link: "/register/",
        title: "Register",
    },
    {
        icon: "0219-graduation-hat",
        link: "/tutorials/",
        title: "Tutorials",
    },
    {
        icon: "calculator",
        link: "/calculator/",
        title: "Calculator",
    },
    {
        icon: "envelope-o",
        link: "/contact/",
        title: "Support",
    },
    homePage,
];

interface LogoProps {
    isMenuOpen: boolean;
}

const Logo = (props: LogoProps) => (
    <motion.svg
        initial={false}
        className={styles.logo}
        animate={props.isMenuOpen === true ? "open" : "closed"}
        variants={variants.logo}
        viewBox="0 0 181 61"
    >
        <motion.path
            initial={false}
            d="M113.1,26 C113.386288,27.4487218 113.52035,28.9234021 113.5,30.4 C113.472355,42.7435186 103.443556,52.7275127 91.1000375,52.6998882 C78.7565188,52.6722638 68.7725081,42.6434817 68.8001119,30.299963 C68.8277158,17.9564442 78.8564812,7.97241675 91.2,8 C96.1576181,8.02719484 100.96977,9.67807899 104.9,12.7 L110.6,7 C98.8399439,-2.61729776 81.7710663,-2.02053704 70.711382,8.39458264 C59.6516976,18.8097023 58.0322912,35.8120618 66.9269572,48.1277531 C75.8216232,60.4434445 92.470938,64.2517275 105.83509,57.0274096 C119.199243,49.8030917 125.132224,33.7873984 119.7,19.6 L113.1,26 Z"
            variants={{
                open: {
                    rotate: 0,
                },
                closed: {
                    rotate: 180,
                    originX: "center",
                    originY: "center",
                },
            }}
            fill="#373A3C"
            className="dark:fill-slate-100"
        />

        <motion.path
            initial={false}
            variants={{
                open: {
                    scale: 1,
                },
                closed: {
                    originX: "center",
                    originY: "center",
                    scale: 0,
                },
            }}
            d="M113.1,9.3 L92.6,29.7 L88,25.1 L82.1,31 L92.6,41.5 L118.1,16.2 C116.782176,13.6663504 115.097326,11.3412568 113.1,9.3 Z"
            id="Path"
            fill="#F57E5F"
        />

        <polygon
            fill="#373A3C"
            className="dark:fill-slate-100"
            points="180.9 0.3 159.2 41.1 159.2 60.2 151.1 60.2 151.1 41.1 129.6 0.1 138.8 0.1 155.2 29.3 171.7 0.2"
        />
        <path
            d="M19.9,60.9 C6.2,60.9 0,51 0,41.1 L8.1,41.1 C8.1,51.8 17,52.9 19.7,52.9 C25.538796,52.9171573 30.3069981,48.2380805 30.4,42.4 L30.4,0.3 L38.5,0.3 L38.5,41.6 C38.6889161,46.5507122 36.8949236,51.372218 33.5158418,54.9953447 C30.1367599,58.6184713 25.451852,60.7437865 20.5,60.9 L19.9,60.9 Z"
            fill="#373A3C"
            className="dark:fill-slate-100"
        />
    </motion.svg>
);

interface Props {
    toggleMenu: () => void;
    isMenuOpen: boolean;
    profile: server.core.rpc.Profile.output | null;
    platform: Platform;
}

export const Menu = (props: Props) => {
    const handleClick = () => props.toggleMenu();
    const swallowClick = (event: React.MouseEvent<HTMLDivElement>) =>
        event.stopPropagation();

    return (
        <motion.button
            initial={false}
            animate={props.isMenuOpen === true ? "open" : "closed"}
            variants={variants.menu}
            className={classNames(styles.menu, props.isMenuOpen && styles.isMenuOpen)}
            onClick={handleClick}
        >
            <motion.div
                className={styles.menuContent}
                variants={variants.menuContent}
                onClick={swallowClick}
            >
                <Logo isMenuOpen={props.isMenuOpen} />
                <motion.ul className={styles.menuItems} variants={variants.menuItems}>
                    {(props.profile != null ? AUTHENTICATED_LINKS : ANONYMOUS_LINKS)
                        .filter(
                            (link) =>
                                link.showIf == null ||
                                link.showIf(props.profile, props.platform) === true,
                        )
                        .map((item, index) => {
                            const i = index % colors.length;
                            const itemStyle = {
                                color: colors[i],
                                // backgroundColor: bgs[i],
                            };

                            const content = (
                                <>
                                    <div
                                        style={itemStyle}
                                        className={styles.menuItemIcon}
                                    >
                                        <Icon large="relative" icon={item.icon} />
                                    </div>
                                    <div className={styles.menuItemTitle}>
                                        {item.title}
                                    </div>
                                </>
                            );
                            const commonProps = {
                                className: styles.menuItemLink,
                                onClick: handleClick,
                            };

                            const link =
                                item.external === true ? (
                                    <a href={item.link} {...commonProps}>
                                        {content}
                                    </a>
                                ) : (
                                    <Link url={item.link} {...commonProps}>
                                        {content}
                                    </Link>
                                );
                            return (
                                <motion.li
                                    key={item.link}
                                    className={styles.menuItem}
                                    variants={variants.menuItemSimple}
                                    whileHover={{scale: 1.05}}
                                    whileTap={{scale: 0.95}}
                                >
                                    {link}
                                </motion.li>
                            );
                        })}
                </motion.ul>
            </motion.div>
        </motion.button>
    );
};
